// NotesSection.jsx — a `notes` field (catalog_categories / catalog_families /
// products), rendered as Markdown when read, plain text when edited. DS-4:
// "a description/notes window… openable like 3D MODEL, on a family or
// product." Alex, 2026-08-21: the first card in the catalog screen's own
// right panel — the panel already existed (the chat lives at its bottom);
// this fills its previously-empty top slot.
//
// Deliberately NOT catalog_lines.notes — that column is a pre-existing,
// differently-scoped field (service-tag / audit-trail text, STATUS.md:
// "792/1198 lines carry a note… 87 of those are live flags"). This section
// only ever reads/writes a CATEGORY/FAMILY/PRODUCT's own `notes` column; the
// caller decides which row that is (see CatalogView's notesTarget) — this
// component just renders + edits whatever text and onSave it's given.
//
//   notes       — current stored text (null/'' reads as empty)
//   onSave      — async (text) => void; throwing surfaces the message inline
//   storageKey  — Card's own persistence key (open/closed) — per SURFACE,
//                 same convention as OnshapeSection, not per item.
//   defaultOpen — Alex, 2026-08-21: "open by default" — explicit here
//                 rather than relying on Card's own default, since a caller
//                 reading this file should not have to also know Card's.
//   applicable, naReason — Card's own two-axis contract (UI_VISION §4):
//                 nothing selected yet is "not applicable", same greyed/
//                 closed/#NA state the 3D Model section already uses.
//   itemLabel   — the selected item's own name, e.g. "Bolts & Gaskets" — fed
//                 straight into Card's `count`, so it renders in the SAME
//                 muted "(…)" the rest of the app already uses for a count,
//                 next to the title (Alex, 2026-08-21: "it is not super
//                 obvious what the note is referring to… the same grey in
//                 parentheses standard for the card, the name… of the
//                 selected item"). Card's count already accepts any string —
//                 the Sizes/Used-in-products cards do the same thing with
//                 "nothing selected" — so this is a plain passthrough, not a
//                 new mechanism.
import { useState, useEffect } from 'react';
import Card from './Card.jsx';
import Markdown from './Markdown.jsx';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_DISABLED } from '../theme-tokens.js';
import { WARN, EDIT } from '../icons.js';

export default function NotesSection({
  notes, onSave, storageKey, defaultOpen = true, applicable = true, naReason = null, itemLabel = null,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // A fresh item (new key from the caller — see CatalogView's remount-on-
  // selection-change) always opens on ITS OWN stored text, never mid-edit
  // on the previous one's draft.
  useEffect(() => {
    setDraft(notes || '');
    setEditing(false);
    setError(null);
  }, [notes]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card storageKey={storageKey} title="Notes" count={itemLabel} defaultOpen={defaultOpen} applicable={applicable} naReason={naReason}>
      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            rows={8}
            placeholder="Markdown — lists, *emphasis*, [links](…) all render."
            style={{
              fontSize: 'var(--fs-body)', padding: '6px 8px', resize: 'vertical',
              border: '1px solid var(--th-border-subtle)', borderRadius: 3, fontFamily: 'inherit',
            }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={save} disabled={saving} style={{ ...BTN_PRIMARY, ...(saving ? BTN_DISABLED : null) }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={() => { setDraft(notes || ''); setEditing(false); setError(null); }}
              disabled={saving}
              style={BTN_SECONDARY}
            >Cancel</button>
          </div>
          {error && (
            <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-status-deprecated)' }}>{WARN} {error}</div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
          {notes ? <Markdown text={notes} /> : (
            <span style={{ color: 'var(--th-text-hint)', fontSize: 'var(--fs-body)' }}>No notes yet.</span>
          )}
          <button onClick={() => setEditing(true)} title="Edit notes" style={BTN_SECONDARY}>{EDIT} Edit</button>
        </div>
      )}
    </Card>
  );
}
